import { useState, useEffect, useCallback } from 'react';
import type { PipelineWeek, AgentStatus } from '../lib/index';
import { mockPipelineWeeks, mockAgentTasks } from '../data/index';

// Agent task progress state
interface AgentTaskProgress {
  agentId: string;
  agentName: string;
  progress: number; // 0-100
  status: 'idle' | 'running' | 'completed' | 'error';
  currentTask?: string;
}

// Distribution status for each platform
interface DistributionStatus {
  platform: string;
  status: 'pending' | 'posting' | 'completed' | 'error';
  progress: number; // 0-100
  postedCount?: number;
  totalCount?: number;
}

// Pipeline approval status
type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export function usePipeline() {
  // Current week state
  const [currentWeek, setCurrentWeek] = useState<PipelineWeek | null>(
    mockPipelineWeeks.find(week => week.status === 'in-progress') || mockPipelineWeeks[0]
  );

  // Approval status
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>('pending');

  // Agent task progress
  const [agentProgress, setAgentProgress] = useState<AgentTaskProgress[]>([]);

  // Distribution status
  const [distributionStatus, setDistributionStatus] = useState<DistributionStatus[]>([]);

  // Agent running state
  const [isAgentRunning, setIsAgentRunning] = useState(false);

  // Distribution running state
  const [isDistributing, setIsDistributing] = useState(false);

  // Initialize agent progress from mock data
  useEffect(() => {
    const initialProgress: AgentTaskProgress[] = mockAgentTasks.map(agent => ({
      agentId: agent.id,
      agentName: agent.name,
      progress: 0,
      status: 'idle',
      currentTask: undefined as string | undefined,
    }));
    setAgentProgress(initialProgress);
  }, []);

  // Simulate agent progress animation
  useEffect(() => {
    if (!isAgentRunning) return;

    const interval = setInterval(() => {
      setAgentProgress(prev => {
        const updated = prev.map(agent => {
          if (agent.status === 'completed') return agent;

          // Random progress increment (5-15%)
          const increment = Math.floor(Math.random() * 10) + 5;
          const newProgress = Math.min(agent.progress + increment, 100);

          // Update status based on progress
          let newStatus: AgentTaskProgress['status'] = 'running';
          if (newProgress >= 100) {
            newStatus = 'completed';
          }

          // Get current task from mock data
          const mockAgent = mockAgentTasks.find(a => a.id === agent.agentId);
          const currentTask = mockAgent?.currentTask || `Processing ${agent.agentName} tasks...`;

          return {
            ...agent,
            progress: newProgress,
            status: newStatus,
            currentTask: newStatus === 'running' ? currentTask : undefined,
          };
        });

        // Check if all agents completed
        const allCompleted = updated.every(agent => agent.status === 'completed');
        if (allCompleted) {
          setIsAgentRunning(false);
        }

        return updated;
      });
    }, 800); // Update every 800ms

    return () => clearInterval(interval);
  }, [isAgentRunning]);

  // Simulate distribution progress animation
  useEffect(() => {
    if (!isDistributing) return;

    const interval = setInterval(() => {
      setDistributionStatus(prev => {
        const updated = prev.map(dist => {
          if (dist.status === 'completed') return dist;

          // Random progress increment (8-20%)
          const increment = Math.floor(Math.random() * 12) + 8;
          const newProgress = Math.min(dist.progress + increment, 100);

          // Update status based on progress
          let newStatus: DistributionStatus['status'] = 'posting';
          if (newProgress >= 100) {
            newStatus = 'completed';
          }

          return {
            ...dist,
            progress: newProgress,
            status: newStatus,
            postedCount: Math.floor((newProgress / 100) * (dist.totalCount || 0)),
          };
        });

        // Check if all distributions completed
        const allCompleted = updated.every(dist => dist.status === 'completed');
        if (allCompleted) {
          setIsDistributing(false);
        }

        return updated;
      });
    }, 600); // Update every 600ms

    return () => clearInterval(interval);
  }, [isDistributing]);

  // Trigger agent run
  const triggerAgentRun = useCallback(() => {
    if (isAgentRunning) return;

    setIsAgentRunning(true);
    setApprovalStatus('pending');

    // Reset all agent progress
    setAgentProgress(prev =>
      prev.map(agent => ({
        ...agent,
        progress: 0,
        status: 'running',
        currentTask: `Starting ${agent.agentName}...`,
      }))
    );
  }, [isAgentRunning]);

  // Approve pipeline
  const approvePipeline = useCallback(() => {
    setApprovalStatus('approved');

    // Update current week status
    if (currentWeek) {
      setCurrentWeek({
        ...currentWeek,
        status: 'review',
      });
    }
  }, [currentWeek]);

  // Reject pipeline
  const rejectPipeline = useCallback(() => {
    setApprovalStatus('rejected');

    // Reset agent progress
    setAgentProgress(prev =>
      prev.map(agent => ({
        ...agent,
        progress: 0,
        status: 'idle',
        currentTask: undefined as string | undefined,
      }))
    );
  }, []);

  // Trigger distribution to a specific platform
  const triggerDistribution = useCallback(
    (platform: string, contentCount: number = 5) => {
      if (approvalStatus !== 'approved') {
        console.warn('Pipeline must be approved before distribution');
        return;
      }

      setIsDistributing(true);

      // Check if platform already exists in distribution status
      const existingPlatform = distributionStatus.find(d => d.platform === platform);

      if (existingPlatform) {
        // Update existing platform
        setDistributionStatus(prev =>
          prev.map(dist =>
            dist.platform === platform
              ? {
                  ...dist,
                  status: 'posting',
                  progress: 0,
                  postedCount: 0,
                  totalCount: contentCount,
                }
              : dist
          )
        );
      } else {
        // Add new platform
        setDistributionStatus(prev => [
          ...prev,
          {
            platform,
            status: 'posting',
            progress: 0,
            postedCount: 0,
            totalCount: contentCount,
          },
        ]);
      }
    },
    [approvalStatus, distributionStatus]
  );

  // Trigger distribution to all platforms
  const triggerAllDistributions = useCallback(() => {
    if (approvalStatus !== 'approved') {
      console.warn('Pipeline must be approved before distribution');
      return;
    }

    setIsDistributing(true);

    const platforms = ['instagram', 'youtube', 'linkedin', 'tiktok', 'pinterest', 'twitter', 'blog'];
    const newDistributions: DistributionStatus[] = platforms.map(platform => ({
      platform,
      status: 'posting',
      progress: 0,
      postedCount: 0,
      totalCount: Math.floor(Math.random() * 5) + 3, // 3-7 content pieces
    }));

    setDistributionStatus(newDistributions);
  }, [approvalStatus]);

  // Reset distribution status
  const resetDistribution = useCallback(() => {
    setDistributionStatus([]);
    setIsDistributing(false);
  }, []);

  // Get overall pipeline progress
  const getOverallProgress = useCallback(() => {
    if (agentProgress.length === 0) return 0;

    const totalProgress = agentProgress.reduce((sum, agent) => sum + agent.progress, 0);
    return Math.floor(totalProgress / agentProgress.length);
  }, [agentProgress]);

  // Get distribution progress
  const getDistributionProgress = useCallback(() => {
    if (distributionStatus.length === 0) return 0;

    const totalProgress = distributionStatus.reduce((sum, dist) => sum + dist.progress, 0);
    return Math.floor(totalProgress / distributionStatus.length);
  }, [distributionStatus]);

  return {
    // State
    currentWeek,
    approvalStatus,
    agentProgress,
    distributionStatus,
    isAgentRunning,
    isDistributing,

    // Actions
    triggerAgentRun,
    approvePipeline,
    rejectPipeline,
    triggerDistribution,
    triggerAllDistributions,
    resetDistribution,

    // Computed
    overallProgress: getOverallProgress(),
    distributionProgress: getDistributionProgress(),
  };
}
